import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Text } from '@bonusly/design-system';

import { Bizy } from '../../bizy';
import type { WhatsNewTip } from '../hooks/use-admin-whats-new-tip';

interface BizyAdminWhatsNewTipProps {
	tip: WhatsNewTip;
	onSelectPrompt: (prompt: string) => void;
}

export const BizyAdminWhatsNewTip: FC<BizyAdminWhatsNewTipProps> = ({ tip, onSelectPrompt }) => {
	const { t } = useTranslation('admin_bizy');

	const promptText = t(tip.promptKey);

	const handleClick = () => {
		onSelectPrompt(promptText);
	};

	return (
		<Flex direction="column" align="center" gap={6} p={6} borderRadius="16px" w="100%" maxW="300px" mx="auto">
			{/* Gradient colors are intentionally fixed per Figma design spec - these brand
			   gradients should remain consistent across light/dark mode for visual continuity */}
			<Box
				position="relative"
				w="120px"
				h="120px"
				borderRadius="full"
				bg="linear-gradient(180deg, #E8F4FF 0%, #F5E6FF 100%)"
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				<Bizy state={tip.state} size={80} />
			</Box>

			<Flex direction="column" align="center" gap={2}>
				<Text fontSize="sm" lineHeight="20px" color="text.soft">
					{t('whatsNew.label')}
				</Text>
				<Text fontSize="lg" lineHeight="24px" fontWeight="semibold" color="text.default" textAlign="center">
					{t(tip.headlineKey)}
				</Text>
			</Flex>

			<Flex direction="column" align="stretch" w="100%" gap={2}>
				<Text fontSize="sm" color="text.soft" textAlign="center">
					{t('whatsNew.tryItOut')}
				</Text>
				<Box
					as="button"
					onClick={handleClick}
					p={3}
					borderRadius="12px"
					border="1px solid"
					borderColor="transparent"
					bg="bg.surface"
					position="relative"
					cursor="pointer"
					transition="all 0.2s ease"
					_hover={{
						bg: 'bg.canvas',
					}}
					// Gradient border colors are fixed per Figma design spec
					_before={{
						content: '""',
						position: 'absolute',
						inset: '-1px',
						borderRadius: '13px',
						padding: '1px',
						background: 'linear-gradient(135deg, #C5E8BA 0%, #70C481 25%, #B3EBE8 50%, #9783EA 75%, #C5E8BA 100%)',
						WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
						WebkitMaskComposite: 'xor',
						maskComposite: 'exclude',
						pointerEvents: 'none',
					}}
				>
					<Text fontSize="sm" color="text.softest" textAlign="center">
						{promptText}
					</Text>
				</Box>
			</Flex>
		</Flex>
	);
};

BizyAdminWhatsNewTip.displayName = 'BizyAdminWhatsNewTip';
